import { useEffect } from 'react';
import { toast } from 'sonner';
import { getWebSocketBaseUrl } from '@/lib/config';

type AdminFeedMessage = {
  type?: string;
  event?: string;
  notification?: {
    id?: string;
    user_id?: string;
    title?: string;
    notif_type?: string;
  };
};

/**
 * Staff-only WebSocket: server pushes when any user receives a new stored notification.
 * ``onUpdate`` should refetch admin stats + notification list.
 */
export function useAdminNotificationsWebSocket(
  enabled: boolean,
  onUpdate: () => void,
) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const token = window.localStorage.getItem('access_token');
    if (!token) return;

    const base = getWebSocketBaseUrl();
    if (!base) return;

    const qs = new URLSearchParams({ token });
    const url = `${base}/ws/admin/notifications/?${qs.toString()}`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      return undefined;
    }

    socket.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data) as AdminFeedMessage;
        if (data.type !== 'admin_notification_feed' || data.event !== 'notification_created') return;
        const title = data.notification?.title;
        const uid = data.notification?.user_id;
        toast.info(
          title
            ? `New notification${uid ? ` (user ${uid})` : ''}: ${title}`
            : 'New notification recorded',
        );
        onUpdate();
      } catch {
        /* ignore */
      }
    };

    socket.onerror = () => {
      /* non-fatal */
    };

    return () => {
      socket.close();
    };
  }, [enabled, onUpdate]);
}
